import { Form, Formik } from 'formik';
import React, { useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { RootState } from 'src/reducers';

import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import * as yup from 'Utilities/yup';

import LibreAppWrapper from 'Containers/LibreAppWrapper';

import ConnectionDetails from 'Components/ConnectionDetails';
import ErrorField from 'Components/input/ErrorField';
import List from 'Components/List';

import LLULogoSmall from 'Images/logos/libreLinkUp-small.svg';
import LLULogoSmallAR from 'Images/logos/libreLinkUp-small-ar.svg';

const addConnection = () => {
  mediator.publish('router:navigate', '/librelinkup/addConnection');
};

const goToConnected = (id: string) => {
  mediator.publish('router:navigate', '/librelinkup/connected/' + id);
};

const goToPending = (id: string) => {
  mediator.publish('router:navigate', '/librelinkup/pending/' + id);
};

const goToMain = () => {
  mediator.publish('router:navigate', '/librelinkup');
};

const resendInvite = (event: React.MouseEvent, invitationId: string) => {
  return new Promise((resolve, reject) => {
    if (invitationId) {
      return mediator
        .publish('llu:resendInvite', { invitationId })
        .then(resolve)
        .catch(reject);
    } else {
      reject();
    }
  });
};

const stopSharing = (event: React.MouseEvent, id: string, isInvite: boolean) => {
  if (id) {
    mediator.publish('llu:stopSharingModal', {
      connectionId: id,
      isInvite,
      callback: (result: LVApiResponse) => {
        if (result.status === 0) {
          mediator.publish('router:navigate', '/librelinkup');
        }
      },
    });
  }
};

const mapState = ({
  config: { lluHome },
  llu: { connections, pending, recentlyInvited },
  nav: { location },
  user: { phoneLanguage },
}: RootState) => {
  return {
    atAddConnection: !!location.match(/^\/librelinkup\/addConnection\/?$/i),
    atConnected: !!location.match(/^\/librelinkup\/connected\//i),
    atMain: !!location.match(/^\/librelinkup\/?$/i),
    atPending: !!location.match(/^\/librelinkup\/pending\//i),
    connected: [...connections],
    currentConnectionId: location.split('/')[3],
    libreLinkUp: {
      id: 'librelinkup',
      learnMore: lluHome,
      logo: phoneLanguage.startsWith('ar') ? LLULogoSmallAR : LLULogoSmall,
      transGroupKey: 'LLU',
    },
    pending: [...pending],
    recentlyInvited,
  };
};

const connector = connect(mapState);

type Props = ConnectedProps<typeof connector>;

const LibreLinkUp: React.FC<React.PropsWithChildren<Props>> = ({
  atAddConnection,
  atConnected,
  atMain,
  atPending,
  connected,
  currentConnectionId,
  libreLinkUp,
  pending,
  recentlyInvited,
}) => {
  // See if the current pending connection has been recently invited
  const inviteResent = useMemo(() => recentlyInvited.includes(currentConnectionId), [
    currentConnectionId,
    recentlyInvited,
  ]);
  // Retrieve the connected or pending details for the selected connection
  const selectedConnection = useMemo(() => {
    return connected.find((c) => c.ConnectionID === currentConnectionId);
  }, [connected, currentConnectionId]);
  const selectedInvite = useMemo(() => {
    return pending.find((c) => c.id === currentConnectionId);
  }, [pending, currentConnectionId]);

  // Collect all connection emails, trimmed and lowercased, for validation
  const usedEmails = useMemo(() => {
    return connected
      .map((c) => c.CaregiverEmail.trim().toLowerCase())
      .concat(pending.map((p) => p.receiver.email.trim().toLowerCase()));
  }, [connected, pending]);

  let content: React.ReactNode = null;

  if (atMain) {
    content = (
      <>
        {/* Main description learn more */}
        <span id="mainDesc-text" className="line-height-xxl mb-2">
          {i18n.t([
            `${libreLinkUp.transGroupKey}.mainDesc`,
            `${libreLinkUp.transGroupKey}.brandDesc`,
          ])}
        </span>

        <a
          id="learnMore-link"
          className="btn btn-link align-self-start line-height-xxl p-0 mb-4"
          href={libreLinkUp.learnMore}
        >
          {i18n.t('Common.learnMoreAbout', {
            name: i18n.t(`${libreLinkUp.transGroupKey}.brandName`),
          })}
        </a>

        {(connected.length > 0 || pending.length > 0) && (
          <>
            {pending.length > 0 && (
              <List
                id="pendingConnectionsList"
                className="mb-3"
                title={i18n.t('LLU.pendingConnections', { context: 'cap' })}
                itemAction={goToPending}
                items={pending.map((p) => ({ id: p.id, ...p.receiver }))}
              />
            )}
            {connected.length > 0 && (
              <List
                id="connectedConnectionsList"
                className="mb-3"
                title={i18n.t('Common.connections', { context: 'cap' })}
                itemAction={goToConnected}
                items={connected.map((c) => ({
                  id: c.ConnectionID,
                  firstName: c.CaregiverFirstName,
                  lastName: c.CaregiverLastName,
                }))}
              />
            )}
          </>
        )}

        <button
          id="addConnection-btn"
          className="btn btn-lg btn-primary w-100 mt-3"
          disabled={connected.length + pending.length >= 20}
          aria-disabled={connected.length + pending.length >= 20}
          onClick={addConnection}
        >
          {i18n.t('LLU.addConnection', { context: 'cap' })}
        </button>
      </>
    );
  } else if (atAddConnection) {
    content = (
      <Formik
        initialValues={{
          email: '',
          firstName: '',
          lastName: '',
        }}
        initialStatus={['firstName', 'lastName', 'email']}
        validationSchema={yup.object({
          email: yup
            .string()
            .required(i18n.t('Errors.requiredEmail'))
            .email(i18n.t('Errors.invalidEmail'))
            .max(255, i18n.t('Errors.genericMaxLength', { count: 255 }))
            // Lowercase and trim the email for comparison to used emails
            .lowercase()
            .trim()
            .notOneOf(usedEmails, i18n.t('Errors.alreadyConnectedAccount')),
          firstName: yup
            .string()
            .required(i18n.t('Errors.requiredFirstName'))
            .limitChar()
            .max(40, i18n.t('Errors.genericMaxLength', { count: 40 })),
          lastName: yup
            .string()
            .required(i18n.t('Errors.requiredLastName'))
            .limitChar()
            .max(80, i18n.t('Errors.genericMaxLength', { count: 80 })),
        })}
        onSubmit={(values, formikHelpers) => {
          mediator
            .request<LVApiResponse>('llu:addConnection', {
              email: values.email.trim(),
              firstName: values.firstName,
              lastName: values.lastName,
            })
            .then((result) => {
              if (result.status === 0) {
                mediator.publish('router:navigate', '/librelinkup/pending/' + result.data.id);
                return;
              }

              // Out-of-region
              if (result.status === 2) {
                formikHelpers.setFieldError('email', i18n.t('Errors.wrongRegionAccount'));
              }

              // Prevent invitation to Pro/HCP users
              if (result.status === 5) {
                formikHelpers.setFieldError('email', i18n.t('Errors.invalidAccountLLU'));
              }

              formikHelpers.setSubmitting(false);
            })
            .catch(() => {
              formikHelpers.setSubmitting(false);
            });
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <div className="d-flex flex-column">
              <span id="addConnection-text" className="text-xl line-height-md mb-4">
                {i18n.t('LLU.addConnection')}
              </span>

              <div className="d-flex flex-column mb-4 md-form">
                {/* First name */}
                <label id="firstName-label" className="text-soft text-md" htmlFor="firstName-field">
                  {i18n.t('LLU.firstName')}
                </label>
                <ErrorField
                  id="firstName-field"
                  autoComplete="off"
                  className="form-control"
                  name="firstName"
                  type="text"
                />

                {/* Last name */}
                <label id="lastName-label" className="text-soft text-md" htmlFor="lastName-field">
                  {i18n.t('LLU.lastName')}
                </label>
                <ErrorField
                  id="lastName-field"
                  autoComplete="off"
                  className="form-control"
                  name="lastName"
                  type="text"
                />

                {/* Email */}
                <label id="email-label" className="text-soft text-md" htmlFor="email-field">
                  {i18n.t('LLU.emailAddress')}
                </label>
                <ErrorField
                  id="email-field"
                  // Disable various html5 features for improved UX when entering emails
                  autoCapitalize="off"
                  autoComplete="off"
                  autoCorrect="off"
                  className="form-control"
                  name="email"
                  spellCheck="false"
                  type="email"
                />
              </div>

              <div className="row">
                <div className="col">
                  <button
                    id="cancelInvite-btn"
                    className="btn btn-lg btn-block btn-secondary"
                    onClick={goToMain}
                    type="button"
                  >
                    {i18n.t('Common.cancel', { context: 'cap' })}
                  </button>
                </div>
                <div className="col">
                  <button
                    id="submitInvite-btn"
                    className="btn btn-lg btn-block btn-primary"
                    disabled={isSubmitting}
                    aria-disabled={isSubmitting}
                    type="submit"
                  >
                    {i18n.t('Common.add', { context: 'cap' })}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    );
  } else if (atConnected) {
    if (selectedConnection) {
      content = (
        <div id={`connectedCaregiver-${currentConnectionId}`} className="d-flex flex-column">
          <div className="d-flex flex-column mb-4">
            <div className="d-flex mb-2">
              <span id="connectedHeader-text" className="text-black text-xl line-height-md">
                {i18n.t('Common.connected')}
              </span>
            </div>
            <span id="connectedDesc-text" className="text-soft line-height-xl">
              {i18n.t('LLU.connectedDesc')}
            </span>
          </div>

          <ConnectionDetails {...selectedConnection} />

          <div className="d-flex justify-content-end mt-3">
            <button
              id="stopSharing-btn"
              className="btn btn-link p-0"
              onClick={(e) => stopSharing(e, currentConnectionId, false)}
            >
              {i18n.t('Common.stopSharing', { context: 'cap' })}
            </button>
          </div>

          <button id="backToApp-btn" className="btn btn-lg btn-primary mt-4" onClick={goToMain}>
            {i18n.t('Common.done', { context: 'cap' })}
          </button>
        </div>
      );
    }
  } else if (atPending) {
    if (selectedInvite) {
      content = (
        <div id={`pendingConnection-${currentConnectionId}`} className="d-flex flex-column">
          <div className="d-flex flex-column mb-4">
            <div className="d-flex mb-2">
              <span id="pendingHeader-text" className="text-black text-xl line-height-md">
                {i18n.t('LLU.connectionPending')}
              </span>
            </div>
            <span id="pendingDesc-text" className="text-soft line-height-xl">
              {i18n.t('LLU.connectionPendingDesc')}
            </span>
          </div>

          <ConnectionDetails {...selectedInvite} />

          <div className="d-flex justify-content-end mt-3">
            {inviteResent ? (
              <span id="inviteResent-text" className="text-success font-italic">
                {i18n.t('LLU.invitationSent')}
              </span>
            ) : (
              <button
                id="resendInvite-btn"
                className="btn btn-link p-0"
                onClick={(e) => resendInvite(e, currentConnectionId)}
              >
                {i18n.t('LLU.resendInvite', { context: 'cap' })}
              </button>
            )}
          </div>

          <div className="d-flex justify-content-end mt-3">
            <button
              id="stopSharing-btn"
              className="btn btn-link p-0"
              onClick={(e) => stopSharing(e, currentConnectionId, true)}
            >
              {i18n.t('Common.remove', { context: 'cap' })}
            </button>
          </div>

          <button id="backToApp-btn" className="btn btn-lg btn-primary mt-4" onClick={goToMain}>
            {i18n.t('Common.done', { context: 'cap' })}
          </button>
        </div>
      );
    }
  }

  return (
    <LibreAppWrapper app={libreLinkUp} showBack={atMain || !content}>
      {content}
    </LibreAppWrapper>
  );
};

export default connector(LibreLinkUp);
